import { createStore, get, set, del, keys, values, type UseStore } from "idb-keyval";

const DB_NAME = "vroom-upload-queue";
const STORE_NAME = "uploadQueue";

let store: UseStore | null = null;

function s(): UseStore {
  if (!store) store = createStore(DB_NAME, STORE_NAME);
  return store;
}

export type UploadStatus = "pending" | "uploading" | "failed";

export interface UploadRow {
  tempId: string;
  file: Blob;
  contentType: string;
  filename: string;
  parentDraftKey: string;
  status: UploadStatus;
  retries: number;
  lastError: string | null;
  createdAt: number;
}

export async function enqueueUpload(opts: { file: File; parentDraftKey: string }): Promise<string> {
  const tempId = crypto.randomUUID();
  const row: UploadRow = {
    tempId,
    file: opts.file,
    contentType: opts.file.type,
    filename: opts.file.name,
    parentDraftKey: opts.parentDraftKey,
    status: "pending",
    retries: 0,
    lastError: null,
    createdAt: Date.now(),
  };
  await set(tempId, row, s());
  return tempId;
}

export async function getUpload(tempId: string): Promise<UploadRow | undefined> {
  return get<UploadRow>(tempId, s());
}

export async function listUploads(): Promise<UploadRow[]> {
  return values<UploadRow>(s());
}

export async function updateUpload(
  tempId: string,
  patch: Partial<Omit<UploadRow, "tempId">>,
): Promise<void> {
  const cur = await get<UploadRow>(tempId, s());
  if (!cur) return;
  await set(tempId, { ...cur, ...patch }, s());
}

export async function deleteUpload(tempId: string): Promise<void> {
  await del(tempId, s());
}

export async function clearAllUploads(): Promise<void> {
  const allKeys = await keys<string>(s());
  for (const k of allKeys) {
    await del(k, s());
  }
}

export async function pendingForParent(parentDraftKey: string): Promise<UploadRow[]> {
  const all = await values<UploadRow>(s());
  return all.filter((row) => row.parentDraftKey === parentDraftKey && row.status !== "failed");
}

/** Resets the module-level store handle. For tests only. */
export function __resetStoreForTests(): void {
  store = null;
}
